<template>
    <div>
        <div class="stack-m">
            <p class="stack-xs">Phone Number</p>
            <template v-for="(phone, index) in phoneNumbersAndEmails.phoneNumbers">
                <div class="grid-fitted grid-unstackable stack-s align-items-center">
                    <div class="grid-item inline-s">
                        <div class="field" :class="{'is-error': $v.value.phoneNumbers.$error.$each || hasError}">
                            <div class="field-group">
                                <v-select
                                    class="contact-phone-types no-flex"
                                    :options="phoneType"
                                    :preselect-first="true"
                                    :show-labels="false"
                                    :allow-empty="false"
                                    placeholder="Select"
                                    v-model="phone.label"
                                />
                                <r-input
                                    v-model="phone.value"
                                    @input="updateItem(phoneItem)"
                                    :validate="$v.value.phoneNumbers.$each[index]"
                                />
                                <div class="field-addon no-flex">
                                    <v-radio
                                        group="phoneNumbers"
                                        label="Make Primary"
                                        :value="phone.primary"
                                        :model="true"
                                        @update="updatePrimary(phoneItem, phone)"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="grid-item no-flex">
                        <v-icon-button v-if="phoneNumbersAndEmails.phoneNumbers.length > 1" type="basic" @click="deleteItem(phoneItem, index)"><v-icon icon="trash"/></v-icon-button>
                        <v-icon-button v-else type="basic" disabled><v-icon icon=""/></v-icon-button>
                    </div>
                </div>
            </template>
            <r-button type="link" class="contact-add-phone-email" @click="addItem(phoneItem)">Add New Phone Number</r-button>
        </div>
        <p class="stack-xs">Email Address</p>
        <template v-for="(email, index) in phoneNumbersAndEmails.emails">
            <div class="grid-fitted grid-unstackable stack-s align-items-center">
                <div class="grid-item inline-s">
                    <div class="field" :class="{'is-error': $v.value.emails.$error.$each || hasError}">
                        <div class="field-group">
                            <v-select
                                class="contact-phone-email-types no-flex"
                                :options="emailType"
                                :show-labels="false"
                                :preselect-first="true"
                                :allow-empty="false"
                                placeholder="Select"
                                v-model="email.label"
                            />
                            <r-input
                                v-model="email.value"
                                @input="updateItem(emailItem)"
                                :validate="$v.value.emails.$each[index]"
                            />
                            <div class="field-addon no-flex">
                                <v-radio
                                    group="emails"
                                    label="Make Primary"
                                    :value="email.primary"
                                    :model="true"
                                    @update="updatePrimary(emailItem, email)"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div class="grid-item no-flex">
                    <v-icon-button v-if="phoneNumbersAndEmails.emails.length > 1" type="basic" @click="deleteItem(emailItem, index)"><v-icon icon="trash"/></v-icon-button>
                    <v-icon-button v-else type="basic" disabled><v-icon icon=""/></v-icon-button>
                </div>
            </div>
        </template>
        <r-button type="link" class="contact-add-phone-email" @click="addItem(emailItem)">Add New Email Address</r-button>
    </div>
</template>

<script>
    import {
        vIcon, vIconButton, vRadio, vSelect,
    } from '@/components/form/index';
    import shortid from 'shortid';
    import ContactPhoneEmailItemsModel from '@/common/models/contact-phone-email-items-model';
    import {email, minLength, required} from 'vuelidate/lib/validators';

    export default {
        name: 'contact-phones-emails',
        components: {
            vIcon,
            vRadio,
            vIconButton,
            vSelect,
        },
        props: {
            value: {
                type: Object,
            },
            aggregator: {
                type: Object,
            },
            hasError: {
                type: Boolean,
            },
        },
        validations: {
            value: {
                phoneNumbers: {
                    $each: {
                        value: {
                            required,
                            minLength: minLength(5),
                        },
                    },
                },
                emails: {
                    $each: {
                        value: {
                            required,
                            email,
                        },
                    },
                },
            },
        },
        watch: {
            value() {
                this.phoneNumbersAndEmails = this.getPhoneNumbersAndEmails();
            },
        },
        data() {
            return {
                validationId: shortid.generate(),
                phoneType: ContactPhoneEmailItemsModel.PhoneLabels,
                emailType: ContactPhoneEmailItemsModel.EmailLabels,
                phoneItem: 'phoneNumbers',
                emailItem: 'emails',
                phoneNumbersAndEmails: this.getPhoneNumbersAndEmails(),
            };
        },
        methods: {
            addItem(type) {
                this.phoneNumbersAndEmails[type].push(new ContactPhoneEmailItemsModel());
            },
            deleteItem(type, index) {
                this.phoneNumbersAndEmails[type].splice(index, 1);
                this.updateItem(type);
            },
            updatePrimary(type, item) {
                this.phoneNumbersAndEmails[type].forEach((value) => {
                    if (value.primary === true) {
                        value.primary = false;
                    }
                    item.primary = true;
                });
                this.updateItem(type);
            },
            updateItem(type) {
                this.value[type] = this.phoneNumbersAndEmails[type].filter(({value}) => !!value);
            },
            validate() {
                this.$v.$touch();
                return !this.$v.$invalid && this.$v.$dirty;
            },
            getValidationAggregate() {
                return {
                    name: `contact-${this.validationId}`,
                    rules: [
                        {
                            name: 'Phone number is required and needs to be at least 5 characters long',
                            delegate: () => {
                                this.validate();
                                return this.$v.value.phoneNumbers.$error;
                            },
                        },
                        {
                            name: 'Email is required and must be valid',
                            delegate: () => {
                                this.validate();
                                return this.$v.value.emails.$error;
                            },
                        },
                    ],
                };
            },
            getPhoneNumbersAndEmails() {
                const phoneNumbersAndEmails = {};
                if (this.value.phoneNumbers.length) {
                    phoneNumbersAndEmails.phoneNumbers = this.value.phoneNumbers;
                } else {
                    phoneNumbersAndEmails.phoneNumbers = [new ContactPhoneEmailItemsModel()];
                    phoneNumbersAndEmails.phoneNumbers[0].primary = true;
                }

                if (this.value.emails.length) {
                    phoneNumbersAndEmails.emails = this.value.emails;
                } else {
                    phoneNumbersAndEmails.emails = [new ContactPhoneEmailItemsModel()];
                    phoneNumbersAndEmails.emails[0].primary = true;
                }
                return phoneNumbersAndEmails;
            },
        },
        created() {
            if (this.aggregator) {
                const aggregate = this.getValidationAggregate();
                this.aggregator.registerComponent(aggregate);
            }
        },
    };
</script>
